import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AppErrorScreen } from './AppErrorScreen';

interface Props {
  children: ReactNode;
  onReset: () => void;
  appId: string;
  bundleVersion: string;
  /** SRI hash computed by useBundleLoad. Optional — may be undefined if the
   *  bundle threw before SHA computation finished, or in tests. */
  sri?: string;
}
interface State {
  hasError: boolean;
}

/**
 * Catches uncaught render errors thrown from inside the loaded app bundle.
 *
 * Fix #22: reports to Sentry with bundle context (app_id, bundle_version,
 * sri_hash) and componentStack so Ops can correlate the user's blank-screen
 * report with the right app + bundle revision.
 */
export class ShellErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    Sentry.captureException(err, {
      tags: {
        code: 'bundle.runtime_error',
        app_id: this.props.appId,
        bundle_version: this.props.bundleVersion,
        sri_hash: this.props.sri ?? 'unknown',
      },
      contexts: {
        errorInfo: { componentStack: info.componentStack ?? '' },
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false });
    this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      return <AppErrorScreen code="bundle.runtime_error" onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}
